import React from "react";
import {Icon, MenuItem, Text} from "@blueprintjs/core";
import {ItemListRenderer, ItemRenderer, Select} from "@blueprintjs/select";
import {ManifestStatusType} from "../../../settings/ColorSettings/ColorizedIndicators/types/manifestAttributes";
import {ActiveIcon, FutureIcon, InactiveIcon} from "../../list/components/manifest-filter/ManifestIcons";
import {DEFAULT_POPOVER_MODIFIERS, EmptyValueStrings} from "../../../../constants/constants";
import {StyledList} from "../../details/components/StopLocationSelector";
import styled from "@emotion/styled";
import {DetailButton} from "../../details/ManifestDetail.styles";

interface ManifestStatusSeletorProps {
  onSelectStatus: (status: ManifestStatusType) => void;
  selectedStatus: string | undefined;
  minimal?: boolean;
}

export const getStatusText = (manifestStatusType: string | undefined, minimal: boolean) => {
  let text: string = EmptyValueStrings.unknown;
  switch (manifestStatusType) {
    case ManifestStatusType.Active:
      text = "Active";
      break;
    case ManifestStatusType.Future:
      text = "Future";
      break;
    case ManifestStatusType.Inactive:
      text = "Inactive";
      break;
    default:
      break;
  }
  return minimal ? text.charAt(0) : text;
};

export const getStatusIcon = (manifestStatusType: string | undefined) => {
  switch (manifestStatusType) {
    case ManifestStatusType.Active:
      return <ActiveIcon />;
    case ManifestStatusType.Future:
      return <FutureIcon />;
    case ManifestStatusType.Inactive:
      return <InactiveIcon />;
    default:
      return null;
  }
};

const getStatusTextColor = (manifestStatusType: string | undefined, minimal: boolean) => {
  if (!minimal) return "#797979";
  switch (manifestStatusType) {
    case ManifestStatusType.Active:
      return "#32A467";
    case ManifestStatusType.Future:
      return "#14305A";
    case ManifestStatusType.Inactive:
      return "#6B7280";
    default:
      return "#797979";
  }
};

const itemRenderer: ItemRenderer<ManifestStatusType> = (status, {handleClick, handleFocus, modifiers}) => {
  return (
    <MenuItem
      data-testid="status-item"
      active={modifiers.active}
      disabled={modifiers.disabled}
      key={status}
      onClick={handleClick}
      onFocus={handleFocus}
      icon={getStatusIcon(status)}
      text={getStatusText(status, false)}
      labelElement={modifiers.active && <Icon icon="tick" />}
    />
  );
};

const itemListRenderer: ItemListRenderer<ManifestStatusType> = ({items, renderItem, itemsParentRef}) => (
  <>
    <StyledText>STATUS</StyledText>
    <StyledList data-testid="manifest-status-list" ref={itemsParentRef}>
      {items.map(renderItem)}
    </StyledList>
  </>
);

const ManifestStatusSeletor = ({onSelectStatus, selectedStatus, minimal = false}: ManifestStatusSeletorProps) => {
  return (
    <Select<ManifestStatusType>
      items={[ManifestStatusType.Active, ManifestStatusType.Future, ManifestStatusType.Inactive]}
      onItemSelect={onSelectStatus}
      activeItem={selectedStatus as ManifestStatusType}
      filterable={false}
      popoverProps={{
        minimal: true,
        popoverClassName: `manifest-status-popover ${minimal ? "minimal" : ""}`,
        modifiers: DEFAULT_POPOVER_MODIFIERS
      }}
      itemRenderer={itemRenderer}
      itemListRenderer={!minimal ? itemListRenderer : undefined}
    >
      <DetailButton
        data-testid="manifest-status"
        variant="minimal"
        customMinimal={minimal}
        icon={!minimal && getStatusIcon(selectedStatus)}
        endIcon={<Icon size={minimal ? 14 : 16} icon={minimal ? "caret-down" : "chevron-down"} />}
        color={getStatusTextColor(selectedStatus, minimal)}
      >
        {getStatusText(selectedStatus, minimal)}
      </DetailButton>
    </Select>
  );
};

export default ManifestStatusSeletor;

const StyledText = styled(Text)`
  font-family: "Roboto", sans-serif;
  color: #797979;
  font-weight: 500;
  margin-bottom: 4px;
  margin-left: 8px;
  font-size: 12px;
`;
